import shortUUID from 'short-uuid';
import React, { useState, useRef, useEffect, useContext } from 'react';
import axios from 'axios';
import { WorkspaceContext, UserContext } from '../../objects/Context';
import { ReactComponent as BoltIcon } from '../../assets/icons/dashboard-icon.svg';
import { ReactComponent as SparkleIcon } from '../../assets/icons/chart-icon.svg';
import { ReactComponent as SendIcon } from '../../assets/icons/arrow-send-icon.svg';
import { ReactComponent as VoiceIcon } from '../../assets/icons/voice-icon.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-icon.svg';
import './Copilot.css';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import MessageFormatter from '../MessageFormatter/MessageFormatter.jsx';
import CopilotGreeting from '../CopilotGreeting/CopilotGreeting.jsx';
import StreamingActions from '../StreamingActions/StreamingActions.js';
// Reused Icons are already imported above
import vairoLogo from '../../assets/images/vairo-logo.png';
import Avatar from '../CircleInitials/CircleInitials';

const attachedContextRegex = /\s*<attached_context>\s*\[Uploaded File:\s*(.+?)\][\s\S]*?<\/attached_context>\s*/;

function parseAttachedContext(content) {
  if (typeof content !== 'string') return { displayContent: content, attachedFileName: null };
  const match = content.match(attachedContextRegex);
  if (!match) return { displayContent: content, attachedFileName: null };
  return {
    displayContent: content.replace(attachedContextRegex, '').trim(),
    attachedFileName: match[1].trim(),
  };
}

function Copilot({
    messages, 
    setMessages, 
    thread, 
    createThread, 
    sendMessage, 
    thinking, 
    setThinking, 
    showProcessingMessage, 
    processingMessage, 
    streamingActionData,
    startedTyping,
    clearMessages,
    layoutView = 'canvas',
    toggleLayoutView = () => {},
    showSampleReportToggle = false,
    showTaskDetailsToggle = false,
    onTaskDetailsClick = () => {},
    showAgentHeader = false,
    showClearButton = false,
    agentName = "Dashboard Agent",
    greetingTitle = <>Hello! <span role="img" aria-label="wave">👋</span></>,
    greetingSubtitle = "I'm here to help with your dashboard.<br/>How can I assist?",
    inputText = "Ask Anything...",
    leftActions = null,
    renderAssistantExtras = null,
    renderInputTop = null,
    isSendDisabled = false,
    maxInputLength,
    renderGreeting = null,
    onInputClick = () => {},
    prefillText,
    onConsumePrefill,
    onBeforeSend,
    researchMode = 'normal',
    setResearchMode = () => {},
    showDeepResearchToggle = false,
    onReportSnippetClick,
    themeColor,
    messagesLoading,
}) {
    const { workspace } = useContext(WorkspaceContext);
    const { user } = useContext(UserContext);
    const [inputValue, setInputValue] = useState('');
    const chatEndRef = useRef(null);
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const chatContainerRef = useRef(null);
    const isTransitioning = useRef(false);
    const textareaRef = useRef(null);
    const fullLayoutTextareaRef = useRef(null);
    const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

    // Scroll to bottom when messages change or layout view switches
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'instant' });
        }
    }, [messages, layoutView]);

    // Handle scroll detection for header shrinking
    useEffect(() => {
        const handleScroll = () => {
            if (chatContainerRef.current && !isTransitioning.current) {
                const scrollTop = chatContainerRef.current.scrollTop;
                
                // Use different thresholds for up vs down (hysteresis)
                const scrollDownThreshold = 30;  // Must scroll down past 30px to shrink
                const scrollUpThreshold = 10;    // Must scroll up past 10px to expand
                
                let shouldBeScrolled = isScrolled;
                
                if (!isScrolled && scrollTop > scrollDownThreshold) {
                    // Currently expanded, check if we should shrink
                    shouldBeScrolled = true;
                } else if (isScrolled && scrollTop < scrollUpThreshold) {
                    // Currently shrunk, check if we should expand
                    shouldBeScrolled = false;
                }
                
                // Only update if state needs to change
                if (shouldBeScrolled !== isScrolled) {
                    isTransitioning.current = true;
                    setIsScrolled(shouldBeScrolled);
                    
                    // Block further changes during transition
                    setTimeout(() => {
                        isTransitioning.current = false;
                    }, 350); // Slightly longer than CSS transition
                }
            }
        };

        const container = chatContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        }
    }, [isScrolled]);

    // Setup speech recognition on mount
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const recog = new SpeechRecognition();
            recog.continuous = true;
            recog.interimResults = true;
            recog.lang = 'en-US';
            recog.onresult = (event) => {
                const transcript = Array.from(event.results)
                    .map(result => result[0])
                    .map(result => result.transcript)
                    .join('');
                setInputValue(transcript);
            };
            recog.onerror = (event) => {
                setIsListening(false);
            };
            recog.onend = () => {
                setIsListening(false);
            };
            setRecognition(recog);
        }
    }, []);

    // Auto-resize textarea function
    const autoResizeTextarea = (textarea) => {
        if (!textarea) return;
        
        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';
        
        // Calculate the new height
        const scrollHeight = textarea.scrollHeight;
        const maxHeight = 500; // 500px limit
        const minHeight = 24; // Minimum height
        
        if (scrollHeight <= maxHeight) {
            // Set height to content height if under max
            textarea.style.height = `${Math.max(scrollHeight, minHeight)}px`;
            textarea.style.overflowY = 'hidden';
        } else {
            // Set to max height and enable scrolling
            textarea.style.height = `${maxHeight}px`;
            textarea.style.overflowY = 'auto';
        }
    };

    // Auto-resize textareas when input value changes
    useEffect(() => {
        if (textareaRef.current) {
            autoResizeTextarea(textareaRef.current);
        }
        if (fullLayoutTextareaRef.current) {
            autoResizeTextarea(fullLayoutTextareaRef.current);
        }
    }, [inputValue]);

    // Initial resize on mount
    useEffect(() => {
        if (textareaRef.current) {
            autoResizeTextarea(textareaRef.current);
        }
        if (fullLayoutTextareaRef.current) {
            autoResizeTextarea(fullLayoutTextareaRef.current);
        }
    }, []);

    // Accept external prefill text from parent (e.g., Question Bank)
    useEffect(() => {
        if (typeof prefillText === 'string' && prefillText.length > 0) {
            setInputValue(prefillText);
            if (typeof onConsumePrefill === 'function') {
                onConsumePrefill();
            }
        }
    }, [prefillText]);

    const handleVoiceMessage = () => {
        if (!recognition) {
            alert('Speech recognition is not supported in your browser.');
            return;
        }
        try {
            if (isListening) {
                recognition.stop();
                setIsListening(false);
            } else {
                recognition.start();
                setIsListening(true);
            }
        } catch (error) {
            setIsListening(false);
        }
    };

    const handleInputInteraction = (e) => {
        // Temporarily disabled - allow normal input
        // Show coming soon popup and prevent default behavior
        // e.preventDefault();
        // e.target.blur(); // Remove focus from textarea
        // setShowComingSoonPopup(true);
    };

    const handleSendMessage = async () => {
        // Temporarily disabled - allow normal sending
        // Show coming soon popup and return early
        // setShowComingSoonPopup(true);
        // return;
        
        if (!inputValue.trim() || isSendDisabled) return;
        const userMessageContent = inputValue.trim();
        setInputValue('');
        if (typeof onBeforeSend === 'function') {
            try { onBeforeSend(); } catch (e) { /* noop */ }
        }
        setThinking(true);

        let newThread;
        let msg;
        try {
            if (!thread || !thread.id) {
                console.log("User initiated conversation. Creating new thread.");
                const newThreadId = shortUUID().new();
                msg = createMessage({content: userMessageContent, threadId: newThreadId, direction: 'sent'});
                setMessages((prevMessages) => [...prevMessages, msg]);
                newThread = await createThread(userMessageContent, newThreadId);
            } else {
                msg = createMessage({ content: userMessageContent, threadId: thread.id });
                setMessages((prevMessages) => [...prevMessages, msg]);
            }

            let threadId = newThread ? newThread.id : thread.id;
            await sendMessage({message: msg, threadId: threadId, researchModeOverride: researchMode});
        } catch (error) {
            console.error("Hmm.. something went wrong. Please try again later.");
        }
    }; 

    const createMessage = ({content, threadId, direction}) => {
        const newMessage = {
            id: shortUUID().new(),
            threadId: threadId, 
            content: content,
            direction: direction || 'sent',
            timestamp: new Date().getTime(),
        };
        return newMessage;
    };

    const resetMessages = ({messageIds}) => {
        setMessages((prevMessages) => prevMessages.filter(message => !messageIds.includes(message.id)));
    };



    // Full layout: match Threads page styling/structure
    if (layoutView === 'full') {
        return (
            <div className="copilot-full">
                {messagesLoading && <LoadingScreen message="Loading messages" />}
                <div className="messages">
                    {(!messages || messages?.length === 0) && (renderGreeting ? renderGreeting() : (
                        <CopilotGreeting 
                            subtitle={greetingSubtitle} 
                            layout="grid"
                            columns={3}
                            rows={3}
                            onExampleClick={(ex) => setInputValue(ex)} 
                        />
                    ))}
                    {messages && messages.map((message, index) => {
                        const isUser = message.direction === 'sent';
                        const messageId = message.id || shortUUID().new();
                        const messageKey = message.key || messageId;
                        const prevMessage = index > 0 ? messages[index - 1] : null;
                        const isPrevSameSender = prevMessage && (prevMessage.direction === message.direction);
                        return (
                            <div key={messageKey} className={`message ${isUser ? 'sender' : 'receiver'} ${isPrevSameSender ? 'consecutive' : ''}`}>
                                {!isUser && !isPrevSameSender && (
                                    <div className="avatar">
                                        <img src={vairoLogo} alt="Vairo Logo" />
                                    </div>
                                )}
                                {!isUser && isPrevSameSender && <div className="avatar-placeholder"></div>}
                                <div className="message-bubbles">
                                    {isUser ? (
                                        (() => {
                                            const { displayContent, attachedFileName } = parseAttachedContext(message.content);
                                            return (
                                                <>
                                                    {attachedFileName && (
                                                        <div className="attached-file-chip">
                                                            <span className="attached-file-icon">📄</span>
                                                            <span className="attached-file-name">{attachedFileName}</span>
                                                        </div>
                                                    )}
                                                    <div className="message-bubble">{displayContent}</div>
                                                </>
                                            );
                                        })()
                                    ) : (
                                        <>
                                            {message.content != null && (
                                                <div className="message-container">
                                                    <div className="message-bubble analysis">
                                                        <MessageFormatter 
                                                            message={message.content} 
                                                            isStreaming={thinking && !showProcessingMessage && index === messages.length - 1}
                                                            onReportSnippetClick={onReportSnippetClick}
                                                        />
                                                    </div>
                                                    {renderAssistantExtras && (!thinking || (thinking && index !== messages.length - 1)) && (
                                                        <div className="message-actions">
                                                            {renderAssistantExtras({ msg: message, idx: index, isLast: index === messages.length - 1 })}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {showProcessingMessage && index === messages.length - 1 && (
                                                //<StreamingActions streamingData={streamingActionData || []} />
                                                <div className="processing-container">
                                                    <div className="spinner"></div>
                                                    <pre className="json-text">{processingMessage}</pre>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                {isUser && !isPrevSameSender && (
                                    <div className="avatar">
                                        <Avatar text={user.name} classN="message-initials" />
                                    </div>
                                )}
                                {isUser && isPrevSameSender && <div className="avatar-placeholder"></div>}
                            </div>
                        );
                    })}
                    <div ref={chatEndRef} />
                </div>
                <div className="message-input">
                    <div className="floating-wrapper">
                        {renderInputTop}
                        <textarea
                            ref={fullLayoutTextareaRef}
                            value={inputValue}
                            onClick={(e) => {
                                handleInputInteraction(e);
                                onInputClick();
                            }}
                            onFocus={handleInputInteraction}
                            onChange={e => setInputValue(e.target.value)}
                            placeholder={inputText}
                            maxLength={maxInputLength}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage();
                                }
                            }}
                        />
                        <div className="action-items">
                            <div className="left-items">
                                {showSampleReportToggle && (
                                    <div className={`action-item label ${layoutView === 'canvas' ? 'active' : ''}`} onClick={toggleLayoutView}>
                                        <p className="bank-text">Sample Report</p>
                                    </div>
                                )}
                                {showTaskDetailsToggle && (
                                    <div className={`action-item label`} onClick={onTaskDetailsClick}>
                                        <p className="bank-text">Task Details</p>
                                    </div>
                                )}
                                {leftActions}
                                {showDeepResearchToggle && (
                                    <div className={`action-item research-mode ${researchMode === 'deep-research' ? 'active' : ''}`}>
                                        <p className="bank-text" onClick={() => {
                                            setResearchMode(researchMode === 'deep-research' ? 'normal' : 'deep-research');
                                        }}>Deep Research</p>
                                    </div>
                                )}
                            </div>
                            <div className={`action-item right-item ${isListening ? 'listening' : ''}`}>
                                {inputValue?.trim() && !isListening && !isSendDisabled ? (
                                    <SendIcon className="icon send" onClick={handleSendMessage} />
                                ) : (
                                    <VoiceIcon className={`icon voice ${isListening ? 'active' : ''}`} onClick={handleVoiceMessage} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {showComingSoonPopup && (
                    <div className="popup-overlay" onClick={() => setShowComingSoonPopup(false)}>
                        <div className="coming-soon-popup" onClick={(e) => e.stopPropagation()}>
                            <div className="popup-header">
                                <h3>Coming soon!</h3>
                                <CloseIcon className="close-icon-btn" onClick={() => setShowComingSoonPopup(false)} />
                            </div>
                            <div className="popup-content">
                                <p>Ability to update your cash forecasts with an AI agent is in the works. Check back soon!</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Canvas layout: existing Copilot design
    return (
        <div className='copilot-chat' ref={chatContainerRef} style={themeColor ? { ['--mode-theme-color']: themeColor } : undefined}>
            {messagesLoading && <LoadingScreen message="Loading messages" />}
            {(showAgentHeader || showClearButton) && (
                <div className={`left-container-header ${isScrolled ? 'scrolled' : ''}`}>
                    {showAgentHeader && (
                        <>
                            <div className='avatar'>
                                <BoltIcon className='icon' />
                            </div>
                            <span className='agent-name'>{agentName}</span>
                        </>
                    )}
                    {showClearButton && (
                        <button
                            className='clear-button'
                            onClick={async () => {
                                const confirmed = window.confirm('Are you sure you want to clear all messages in this thread?');
                                if (!confirmed) return;
                                try {
                                    if (thread?.id) {
                                        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/threads/clear-thread/${thread.id}`, {}, { withCredentials: true });
                                    }
                                } catch (e) { /* noop */ }
                                finally {
                                    if (typeof clearMessages === 'function') {
                                        clearMessages();
                                    } else if (typeof setMessages === 'function') {
                                        setMessages([]);
                                    }
                                }
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>
            )}
            <div className='chat-messages'>
                {(!messages || messages?.length === 0) && (
                    renderGreeting ? (
                        renderGreeting()
                    ) : (
                        <CopilotGreeting 
                            subtitle={greetingSubtitle} 
                            layout="grid"
                            columns={1}
                            rows={3}
                            onExampleClick={(ex) => setInputValue(ex)} 
                        />
                    )
                )}
                {messages && messages?.length > 0 && messages.map((msg, idx) =>
                    msg.direction === 'sent' ? (
                        (() => {
                            const { displayContent, attachedFileName } = parseAttachedContext(msg.content);
                            return (
                                <div key={msg.timestamp + idx} className="chat-message user">
                                    <div className="user-message-column">
                                        <div className="chat-bubble user-bubble">{displayContent}</div>
                                        {attachedFileName && (
                                            <div className="attached-file-chip">
                                                <span className="attached-file-icon">📄</span>
                                                <span className="attached-file-name">{attachedFileName}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="user-header">
                                        <div className="user-avatar">{user.name.charAt(0)}</div>
                                    </div>
                                </div>
                            );
                        })()
                    ) : (
                        <div key={msg.timestamp + idx} className="chat-message assistant">
                            <div className="assistant-header">
                                <div className='avatar'>
                                    <img src={vairoLogo} alt="Vairo Logo" />
                                </div>
                            </div>
                            <div className="assistant-content">
                                {msg.content != null && (
                                    <div className="chat-bubble assistant-bubble message-bubble">
                                        <MessageFormatter 
                                            message={msg.content} 
                                            isStreaming={thinking && !showProcessingMessage && idx === messages.length - 1}
                                            onReportSnippetClick={onReportSnippetClick}
                                        />
                                    </div>
                                )}
                                {renderAssistantExtras && (!thinking || (thinking && idx !== messages.length - 1)) && (
                                    <div className="message-actions" style={{ marginTop: '-12px' }}>
                                        {renderAssistantExtras({ msg, idx, isLast: idx === messages.length - 1 })}
                                    </div>
                                )}
                                {showProcessingMessage && idx === messages.length - 1 && (
                                    //<StreamingActions streamingData={streamingActionData || []} />
                                    <div className="processing-container">
                                        <div className="spinner"></div>
                                        <pre className="json-text">{processingMessage}</pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                )}
                <div ref={chatEndRef}></div>
            </div>
            <div className="message-input">
                <div className="floating-wrapper">
                    {renderInputTop}
                    <textarea
                        ref={textareaRef}
                        value={inputValue}
                        onClick={handleInputInteraction}
                        onFocus={handleInputInteraction}
                        onChange={e => setInputValue(e.target.value)}
                        placeholder={inputText}
                        maxLength={maxInputLength}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                    />
                    <div className="action-items">
                        <div className="left-items">
                            {showSampleReportToggle && (
                                <div className={`action-item ${layoutView === 'canvas' ? 'active' : ''}`} onClick={toggleLayoutView}>
                                    <p className="bank-text">Sample Report</p>
                                </div>
                            )}
                            {showTaskDetailsToggle && (
                                <div className={`action-item`} onClick={onTaskDetailsClick}>
                                    <p className="bank-text">Task Details</p>
                                </div>
                            )}
                            {leftActions}
                            {showDeepResearchToggle && (
                                <div className={`action-item research-mode ${researchMode === 'deep-research' ? 'active' : ''}`}>
                                    <p className="bank-text" onClick={() => {
                                        setResearchMode(researchMode === 'deep-research' ? 'normal' : 'deep-research');
                                    }}>Deep Research</p>
                                </div>
                            )}
                        </div>
                        <div className={`action-item right-item ${isListening ? 'listening' : ''}`}> 
                            {inputValue?.trim() && !isListening && !isSendDisabled ? (
                                <SendIcon className="icon send" onClick={handleSendMessage} />
                            ) : (
                                <VoiceIcon 
                                    className={`icon voice ${isListening ? 'active' : ''}`} 
                                    onClick={handleVoiceMessage}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
        </div>
    );
}

export default Copilot;
